import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Footprints, Flame, 
  Calendar, ChevronLeft, ChevronRight, Target, Trophy, Star, Zap
} from 'lucide-react';

// Storage keys
const DAILY_STEPS_KEY = 'walking_daily_steps';
const WORKOUT_HISTORY_KEY = 'workoutHistory';
const STEP_GOAL_KEY = 'walking_step_goal_settings';

// Get week dates (Monday to Sunday)
const getWeekDates = (weekOffset = 0) => {
  const now = new Date();
  const currentDay = now.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
  monday.setHours(0, 0, 0, 0);
  
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
};

// Get month dates
const getMonthDates = (monthOffset = 0) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + monthOffset;
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const dates = [];
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    dates.push(new Date(d).toISOString().split('T')[0]);
  }
  return dates;
};

// Day names for display
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
            {entry.name === 'calories' ? ' kcal' : ''}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function WalkingStatistics({ onBack }) {
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  
  // Load step goal from localStorage
  const stepGoal = useMemo(() => {
    const saved = localStorage.getItem(STEP_GOAL_KEY);
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.enabled ? (settings.goal || 10000) : 10000;
    }
    return 10000;
  }, []);
  
  // Load data from localStorage
  const dailySteps = useMemo(() => {
    const saved = localStorage.getItem(DAILY_STEPS_KEY);
    return saved ? JSON.parse(saved) : {};
  }, []);
  
  const workoutHistory = useMemo(() => {
    const saved = localStorage.getItem(WORKOUT_HISTORY_KEY);
    return saved ? JSON.parse(saved) : [];
  }, []);
  
  // Get dates based on view mode
  const currentDates = useMemo(() => {
    return viewMode === 'week' ? getWeekDates(weekOffset) : getMonthDates(monthOffset);
  }, [viewMode, weekOffset, monthOffset]);
  
  const prevDates = useMemo(() => {
    return viewMode === 'week' ? getWeekDates(weekOffset - 1) : getMonthDates(monthOffset - 1);
  }, [viewMode, weekOffset, monthOffset]);
  
  // Calculate data for current period
  const periodData = useMemo(() => {
    return currentDates.map((date, index) => {
      const steps = dailySteps[date] || 0;
      const dayWorkouts = workoutHistory.filter(w => 
        w.type === 'walking' && w.date && w.date.startsWith(date)
      );
      const calories = dayWorkouts.reduce((sum, w) => sum + (w.calories || 0), 0);
      const workoutCount = dayWorkouts.length;
      
      // For weekly view, use day names; for monthly, use day number
      const label = viewMode === 'week' 
        ? DAY_NAMES[index] 
        : new Date(date).getDate().toString();
      
      return {
        day: label,
        date,
        steps,
        calories,
        workoutCount,
        metGoal: steps >= stepGoal
      };
    });
  }, [currentDates, dailySteps, workoutHistory, viewMode, stepGoal]);
  
  // Calculate previous period data for comparison
  const prevPeriodData = useMemo(() => {
    return prevDates.map((date) => {
      const steps = dailySteps[date] || 0;
      const dayWorkouts = workoutHistory.filter(w => 
        w.type === 'walking' && w.date && w.date.startsWith(date)
      );
      const calories = dayWorkouts.reduce((sum, w) => sum + (w.calories || 0), 0);
      return { steps, calories, metGoal: steps >= stepGoal };
    });
  }, [prevDates, dailySteps, workoutHistory, stepGoal]);
  
  // Calculate totals and stats
  const totalSteps = periodData.reduce((sum, d) => sum + d.steps, 0);
  const totalCalories = periodData.reduce((sum, d) => sum + d.calories, 0);
  const totalWorkouts = periodData.reduce((sum, d) => sum + d.workoutCount, 0);
  const daysWithData = periodData.filter(d => d.steps > 0).length;
  const avgStepsPerDay = daysWithData > 0 ? Math.round(totalSteps / daysWithData) : 0;
  
  // Goal progress tracking
  const daysMetGoal = periodData.filter(d => d.metGoal).length;
  const prevDaysMetGoal = prevPeriodData.filter(d => d.metGoal).length;
  const bestDay = periodData.reduce((best, d) => d.steps > best.steps ? d : best, { steps: 0 });
  const currentStreak = useMemo(() => {
    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    const sortedDates = [...currentDates].reverse();
    
    for (const date of sortedDates) {
      if (date > today) continue;
      const steps = dailySteps[date] || 0;
      if (steps >= stepGoal) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  }, [currentDates, dailySteps, stepGoal]);
  
  const prevTotalSteps = prevPeriodData.reduce((sum, d) => sum + d.steps, 0);
  const prevTotalCalories = prevPeriodData.reduce((sum, d) => sum + d.calories, 0);
  
  // Calculate change percentages
  const stepsChange = prevTotalSteps > 0 
    ? Math.round(((totalSteps - prevTotalSteps) / prevTotalSteps) * 100) 
    : totalSteps > 0 ? 100 : 0;
  const caloriesChange = prevTotalCalories > 0 
    ? Math.round(((totalCalories - prevTotalCalories) / prevTotalCalories) * 100)
    : totalCalories > 0 ? 100 : 0;
  
  // Format period label
  const getPeriodLabel = () => {
    if (viewMode === 'week') {
      if (weekOffset === 0) return 'This Week';
      if (weekOffset === -1) return 'Last Week';
      const startDate = new Date(currentDates[0]);
      const endDate = new Date(currentDates[6]);
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      if (monthOffset === 0) return 'This Month';
      if (monthOffset === -1) return 'Last Month';
      const date = new Date(currentDates[0]);
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
    }
  };
  
  // Navigate periods
  const goToPrev = () => {
    if (viewMode === 'week') {
      setWeekOffset(prev => prev - 1);
    } else {
      setMonthOffset(prev => prev - 1);
    }
  };
  
  const goToNext = () => {
    if (viewMode === 'week') {
      setWeekOffset(prev => Math.min(0, prev + 1));
    } else {
      setMonthOffset(prev => Math.min(0, prev + 1));
    }
  };
  
  const canGoNext = viewMode === 'week' ? weekOffset < 0 : monthOffset < 0;

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-4"
        data-testid="back-from-stats-btn"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      {/* Header with View Mode Toggle */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Walking Statistics</h2>
          <p className="text-muted-foreground">Track your progress</p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('week')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-purple-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="view-week-btn"
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'month' 
                  ? 'bg-purple-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="view-month-btn"
            >
              Monthly
            </button>
          </div>
          
          {/* Period Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-white font-medium min-w-[100px] text-center text-sm">
              {getPeriodLabel()}
            </span>
            <Button variant="outline" size="icon" onClick={goToNext} disabled={!canGoNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Goal Progress Card */}
      <Card className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-yellow-400 mb-3">
            <Trophy className="h-5 w-5" />
            <span className="font-semibold">Goal Progress</span>
            <span className="text-sm text-muted-foreground ml-auto">
              Goal: {stepGoal.toLocaleString()} steps/day
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {/* Days Met Goal */}
            <div className="text-center p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Star className="h-4 w-4 text-yellow-400" />
              </div>
              <div className="text-2xl font-bold text-yellow-400">{daysMetGoal}</div>
              <div className="text-xs text-muted-foreground">
                days at {(stepGoal/1000).toFixed(0)}k+
              </div>
              {daysMetGoal > prevDaysMetGoal && (
                <div className="text-xs text-green-400 mt-1">
                  +{daysMetGoal - prevDaysMetGoal} vs last {viewMode}
                </div>
              )}
            </div>
            
            {/* Current Streak */}
            <div className="text-center p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Zap className="h-4 w-4 text-orange-400" />
              </div>
              <div className="text-2xl font-bold text-orange-400">{currentStreak}</div>
              <div className="text-xs text-muted-foreground">day streak</div>
              {currentStreak >= 3 && (
                <div className="text-xs text-orange-400 mt-1">🔥 On fire!</div>
              )}
            </div>
            
            {/* Best Day */}
            <div className="text-center p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className="h-4 w-4 text-green-400" />
              </div>
              <div className="text-2xl font-bold text-green-400">
                {bestDay.steps > 0 ? `${(bestDay.steps/1000).toFixed(1)}k` : '-'}
              </div>
              <div className="text-xs text-muted-foreground">best day</div>
              {bestDay.metGoal && (
                <div className="text-xs text-green-400 mt-1">✓ Goal met!</div>
              )}
            </div>
          </div>
          
          {/* Goal completion bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Goal completion rate</span>
              <span>{Math.round((daysMetGoal / currentDates.length) * 100)}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(daysMetGoal / currentDates.length) * 100}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Total Steps Card */}
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-purple-400 mb-2">
              <Footprints className="h-5 w-5" />
              <span className="text-sm font-medium">Total Steps</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {totalSteps.toLocaleString()}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {stepsChange >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-400" />
              )}
              <span className={`text-sm ${stepsChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stepsChange >= 0 ? '+' : ''}{stepsChange}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Total Calories Card */}
        <Card className="bg-orange-500/10 border-orange-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-400 mb-2">
              <Flame className="h-5 w-5" />
              <span className="text-sm font-medium">Calories</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {totalCalories.toLocaleString()}
              <span className="text-lg text-muted-foreground ml-1">kcal</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              {caloriesChange >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-400" />
              )}
              <span className={`text-sm ${caloriesChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {caloriesChange >= 0 ? '+' : ''}{caloriesChange}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Average Steps Card */}
        <Card className="bg-cyan-500/10 border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-cyan-400 mb-2">
              <Target className="h-5 w-5" />
              <span className="text-sm font-medium">Daily Avg</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {avgStepsPerDay.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              steps/day
            </div>
          </CardContent>
        </Card>

        {/* Workouts Count Card */}
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <Calendar className="h-5 w-5" />
              <span className="text-sm font-medium">Workouts</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {totalWorkouts}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              sessions
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Steps Chart */}
      <Card className="border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-purple-400 flex items-center gap-2 text-base">
            <Footprints className="h-5 w-5" />
            Daily Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <BarChart data={periodData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="day" 
                  stroke="#9CA3AF"
                  fontSize={10}
                  interval={viewMode === 'month' ? 4 : 0}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  fontSize={10}
                  tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="steps" 
                  fill="#A855F7" 
                  radius={[2, 2, 0, 0]}
                  name="steps"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Calories Chart */}
      <Card className="border-orange-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-orange-400 flex items-center gap-2 text-base">
            <Flame className="h-5 w-5" />
            Daily Calories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <AreaChart data={periodData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="caloriesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="day" 
                  stroke="#9CA3AF"
                  fontSize={10}
                  interval={viewMode === 'month' ? 4 : 0}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  fontSize={10}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone"
                  dataKey="calories" 
                  stroke="#F97316"
                  strokeWidth={2}
                  fill="url(#caloriesGradient)"
                  name="calories"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Daily Breakdown - Only for Weekly View */}
      {viewMode === 'week' && (
        <Card className="border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base">Daily Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {periodData.map((day, index) => {
                const isToday = day.date === new Date().toISOString().split('T')[0];
                return (
                  <div 
                    key={day.date}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isToday ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        day.metGoal ? 'bg-green-500/20' : day.steps > 0 ? 'bg-purple-500/20' : 'bg-slate-700'
                      }`}>
                        {day.metGoal ? (
                          <Star className="h-4 w-4 text-yellow-400" />
                        ) : (
                          <span className={`text-sm font-medium ${
                            day.steps > 0 ? 'text-purple-400' : 'text-slate-500'
                          }`}>
                            {DAY_NAMES[index]}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {isToday && <span className="ml-2 text-xs text-yellow-400">Today</span>}
                        </div>
                        {day.workoutCount > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {day.workoutCount} workout{day.workoutCount > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className={`font-bold ${day.metGoal ? 'text-green-400' : 'text-purple-400'}`}>
                          {day.steps.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">steps</div>
                      </div>
                      <div className="text-right min-w-[60px]">
                        <div className="text-orange-400 font-bold">
                          {day.calories}
                        </div>
                        <div className="text-xs text-muted-foreground">kcal</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data Message */}
      {totalSteps === 0 && totalCalories === 0 && (
        <Card className="border-slate-700">
          <CardContent className="p-8 text-center">
            <Footprints className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Data Yet</h3>
            <p className="text-muted-foreground">
              Start a walking workout to see your statistics here!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
